import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Login2faChallenge } from '../types';
import { isBusinessOwner } from '../utils/roleUtils';
import {
  clearBusiness2faTrustToken,
  getBusiness2faTrustToken,
  isBusiness2faVerifiedThisSession,
  markBusiness2faVerifiedThisSession,
} from '../utils/business2faTrust';
import { apiService } from '../services/api';
import Business2faOtpForm from './Business2faOtpForm';
import CustomerNavbar from './CustomerNavbar';
import Footer from './Footer';

type GateStatus = 'checking' | 'allowed' | 'challenge' | 'error';

/**
 * Gates /dashboard/business* for Business Owners with payment-enabled businesses.
 * Professionals and non-payment owners pass through unchanged.
 * Checks 14-day browser trust (and same-tab session) before sending a code.
 */
const BusinessOwner2faGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<GateStatus>('checking');
  const [challenge, setChallenge] = useState<Login2faChallenge | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const checkIdRef = useRef(0);

  const isBusinessPath = location.pathname.startsWith('/dashboard/business');

  useEffect(() => {
    if (!isBusinessPath) {
      setStatus('allowed');
      setChallenge(null);
      setErrorMessage(null);
      return;
    }

    if (!user) {
      setStatus('allowed');
      return;
    }

    // Professionals / staff without owner role: no change to existing behavior
    if (!isBusinessOwner(user)) {
      setStatus('allowed');
      setChallenge(null);
      return;
    }

    if (isBusiness2faVerifiedThisSession(user.id)) {
      setStatus('allowed');
      setChallenge(null);
      return;
    }

    const checkId = ++checkIdRef.current;
    let cancelled = false;

    const runCheck = async () => {
      setStatus('checking');
      setErrorMessage(null);
      setChallenge(null);

      try {
        const trustToken = user.email ? getBusiness2faTrustToken(user.email) || undefined : undefined;
        const result = await apiService.startBusiness2faChallenge(trustToken);

        if (cancelled || checkId !== checkIdRef.current) return;

        if (!result.required) {
          markBusiness2faVerifiedThisSession(user.id);
          setStatus('allowed');
          return;
        }

        if (user.email) {
          clearBusiness2faTrustToken(user.email);
        }

        setChallenge({
          requires2fa: true,
          challengeId: result.challengeId!,
          email: result.email || user.email,
          expiresInSeconds: result.expiresInSeconds || 600,
        });
        setStatus('challenge');
      } catch (error: any) {
        if (cancelled || checkId !== checkIdRef.current) return;
        setErrorMessage(
          error.response?.data?.message ||
            'Unable to verify business access. Please try again.'
        );
        setStatus('error');
      }
    };

    void runCheck();

    return () => {
      cancelled = true;
    };
  }, [isBusinessPath, user?.id, user?.email, location.pathname]);

  if (!isBusinessPath) {
    return <>{children}</>;
  }

  if (!user || !isBusinessOwner(user) || status === 'allowed') {
    return <>{children}</>;
  }

  const handleCancel = () => {
    navigate('/dashboard/customer', { replace: true });
  };

  const handleRetry = () => {
    // Force effect re-run
    setStatus('checking');
    setErrorMessage(null);
    setChallenge(null);
    checkIdRef.current += 1;
    const trustToken = user.email ? getBusiness2faTrustToken(user.email) || undefined : undefined;
    void (async () => {
      const thisCheck = checkIdRef.current;
      try {
        const result = await apiService.startBusiness2faChallenge(trustToken);
        if (thisCheck !== checkIdRef.current) return;
        if (!result.required) {
          markBusiness2faVerifiedThisSession(user.id);
          setStatus('allowed');
          return;
        }
        setChallenge({
          requires2fa: true,
          challengeId: result.challengeId!,
          email: result.email || user.email,
          expiresInSeconds: result.expiresInSeconds || 600,
        });
        setStatus('challenge');
      } catch (error: any) {
        if (thisCheck !== checkIdRef.current) return;
        setErrorMessage(
          error.response?.data?.message ||
            'Unable to verify business access. Please try again.'
        );
        setStatus('error');
      }
    })();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col no-animations">
      <CustomerNavbar
        onLogout={async () => {
          try {
            await logout();
          } finally {
            navigate('/login?type=customer');
          }
        }}
        showDashboardLink={true}
        showAuthButtons={false}
      />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                <ShieldCheck className="h-10 w-10 text-blue-900" strokeWidth={2} />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Verify your identity</h2>
            <p className="mt-2 text-sm text-gray-600">
              Extra verification is required before opening the business dashboard
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
            {status === 'checking' && (
              <div className="flex flex-col items-center justify-center py-10 text-gray-600">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mb-4" />
                <p className="text-sm">Checking business access...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <p className="text-sm text-red-700">{errorMessage}</p>
                <button type="button" onClick={handleRetry} className="aacore-btn-primary w-full justify-center">
                  Try again
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="aacore-btn-outlined w-full justify-center"
                >
                  Back to My Dashboard
                </button>
              </div>
            )}

            {status === 'challenge' && challenge && (
              <Business2faOtpForm
                challenge={challenge}
                onChallengeChange={setChallenge}
                onVerified={() => {
                  markBusiness2faVerifiedThisSession(user.id);
                  setStatus('allowed');
                  setChallenge(null);
                }}
                onCancel={handleCancel}
                cancelLabel="Back to My Dashboard"
              />
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessOwner2faGuard;
